import type { ReactElement } from "react"

import { useLocation } from "react-router-dom"

import type { PanelProps } from "./panel-props"

import { AppWithLocalIndexSettings } from "./panels/app-with-local-index-settings.tsx"
import { CoreBookmarksSettings } from "./panels/core-bookmarks-settings.tsx"
import { DontHitItSettings } from "./panels/dont-hit-it-settings.tsx"
import { GutenbergSettings } from "./panels/gutenberg-settings.tsx"
import { HaveYouSeenItSettings } from "./panels/have-you-seen-it.tsx"
import { RememberSequenceSettings } from "./panels/remember-sequence-settings.tsx"
import { RevealItSettings } from "./panels/reveal-it-settings.tsx"
import { SelectCorrectSettings } from "./panels/select-correct-settings.tsx"
import { SelectCorrectTranslationSettings } from "./panels/select-correct-translation-settings.tsx"
import { SelectCorrectWordSettings } from "./panels/select-correct-word-settings.tsx"
import { ShortSentencesSettings } from "./panels/short-sentences-settings.tsx"
import { SpeakAppsSettings } from "./panels/speak-apps-settings.tsx"
import { SpeedReaderSettings } from "./panels/speed-reader-settings.tsx"
import { TranslateItSettings } from "./panels/translate-it-settings.tsx"
import { WriteCodeSettings } from "./panels/write-code-settings.tsx"
import { SharedSettings } from "./shared-settings.tsx"

const ROUTE_HANDLERS: {
  match: (pathname: string)=> boolean
  render: (pathname: string, closeSidebar: ()=> void)=> ReactElement
}[] = [
  {
    match: p=> p.startsWith("/speak-words-of-sentence"),
    render: (_, closeSidebar)=> <SpeakAppsSettings closeSidebar={closeSidebar} />,
  },
  {
    match: p=> p.startsWith("/select-correct-translation"),
    render: (_, closeSidebar)=> (
      <SelectCorrectTranslationSettings closeSidebar={closeSidebar} />
    ),
  },
  {
    match: p=> p.startsWith("/gutenberg"),
    render: (p, closeSidebar)=> (
      <GutenbergSettings
        closeSidebar={closeSidebar}
        language={p.endsWith("-french") ? "fr" : "de"}
      />
    ),
  },
  {
    match: p=> p.startsWith("/dont-hit-it"),
    render: (_, closeSidebar)=> <DontHitItSettings closeSidebar={closeSidebar} />,
  },
  {
    match: p=> p.startsWith("/remember-sequence"),
    render: (_, closeSidebar)=> (
      <RememberSequenceSettings closeSidebar={closeSidebar} />
    ),
  },
  {
    match: p=> p === "/select-correct",
    render: (_, closeSidebar)=> (
      <SelectCorrectSettings closeSidebar={closeSidebar} />
    ),
  },
  {
    match: p=> p.startsWith("/select-correct-word"),
    render: (_, closeSidebar)=> (
      <SelectCorrectWordSettings closeSidebar={closeSidebar} />
    ),
  },
  {
    match: p=> p.startsWith("/have-you-seen-it"),
    render: (_, closeSidebar)=> (
      <HaveYouSeenItSettings closeSidebar={closeSidebar} />
    ),
  },
  {
    match: p=> p.startsWith("/short-sentences"),
    render: (_, closeSidebar)=> (
      <ShortSentencesSettings closeSidebar={closeSidebar} />
    ),
  },
  {
    match: p=> p.startsWith("/speed-reader"),
    render: (_, closeSidebar)=> (
      <SpeedReaderSettings closeSidebar={closeSidebar} />
    ),
  },
  {
    match: p=> p.startsWith("/translate-it"),
    render: (_, closeSidebar)=> (
      <TranslateItSettings closeSidebar={closeSidebar} />
    ),
  },
  {
    match: p=> ["/den-vs-dem", "/select-poem-rhyme-word"].includes(p),
    render: (_, closeSidebar)=> (
      <AppWithLocalIndexSettings closeSidebar={closeSidebar} />
    ),
  },
  {
    match: p=> p.startsWith("/reveal-it"),
    render: (_, closeSidebar)=> <RevealItSettings closeSidebar={closeSidebar} />,
  },
  {
    match: p=> p.startsWith("/core-bookmarks"),
    render: (_, closeSidebar)=> (
      <CoreBookmarksSettings closeSidebar={closeSidebar} />
    ),
  },
  {
    match: p=> p.startsWith("/write-code"),
    render: (_, closeSidebar)=> <WriteCodeSettings closeSidebar={closeSidebar} />,
  },
]

export function SidebarContent(input: PanelProps) {
  const location = useLocation()
  return sidebarPanelForPath(location.pathname, input.closeSidebar)
}

function sidebarPanelForPath(
  pathname: string,
  closeSidebar: ()=> void,
): ReactElement {
  for (const { match, render } of ROUTE_HANDLERS) {
    if (match(pathname)) {
      return render(pathname, closeSidebar)
    }
  }
  return <SharedSettings closeSidebar={closeSidebar} />
}